/**
 * Extract ElementData[] from Axos snapshot events.
 *
 * Kept in sync with:
 *   external-api/src/events/listeners/inference/axosElementExtractor.ts
 */

import type { ElementData, ClickPosition } from "./types";

const SKIP_TAGS = new Set([
  "script",
  "style",
  "noscript",
  "link",
  "meta",
  "head",
  "base",
  "title",
]);

const INTERACTIVE_TAGS = new Set([
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "details",
  "summary",
  "label",
  "option",
]);

const BUTTON_TAGS = new Set(["button"]);
const INPUT_TAGS = new Set(["input", "textarea", "select"]);
const LINK_TAGS = new Set(["a"]);

const MAX_ELEMENTS = 200;
const WALK_CAP = 2000;

interface AxosNode {
  tagName?: string;
  attributes?: Array<{ name: string; value: string }>;
  children?: AxosNode[];
  textContent?: string | null;
  nodeId?: number;
}

export function extractAxosElementsFromEvents(
  events: Record<string, unknown>[],
  existingElements: ElementData[],
  clickPositions?: ClickPosition[],
): ElementData[] {
  let latestSnapshot: Record<string, unknown> | null = null;

  for (let i = events.length - 1; i >= 0; i--) {
    const evt = events[i];
    if (evt.type === "snapshot") {
      latestSnapshot = evt;
      break;
    }
  }

  if (!latestSnapshot) return existingElements;

  const payload = latestSnapshot.payload as Record<string, unknown> | undefined;
  if (!payload?.snapshotElement) return existingElements;

  const allElements: ElementData[] = [];
  walkNode(payload.snapshotElement as AxosNode, allElements, -1, 0);

  if (allElements.length === 0) return existingElements;

  return prioritiseElements(allElements, clickPositions ?? []);
}

function getAttr(
  attrs: Array<{ name: string; value: string }>,
  name: string,
): string | undefined {
  return attrs.find((a) => a.name === name)?.value;
}

function parsePx(val: string | undefined): number {
  if (val == null) return 0;
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : 0;
}

function collectDeepText(node: AxosNode, maxLen: number): string {
  const tag = (node.tagName ?? "").toLowerCase();
  if (tag === "#text") return node.textContent ?? "";
  if (tag === "script" || tag === "style" || tag === "svg") return "";

  let result = node.textContent?.trim() ?? "";
  for (const child of node.children ?? []) {
    if (result.length >= maxLen) break;
    result += collectDeepText(child, maxLen - result.length);
  }
  return result;
}

function walkNode(
  node: AxosNode,
  elements: ElementData[],
  parentIdx: number,
  depth: number,
): void {
  if (elements.length >= WALK_CAP) return;

  const rawTag = (node.tagName ?? "").toLowerCase();

  if (rawTag === "#text") return;
  if (!rawTag || SKIP_TAGS.has(rawTag)) return;

  const attrs = node.attributes ?? [];
  const children = node.children ?? [];

  let textContent = "";
  for (const child of children) {
    if ((child.tagName ?? "").toLowerCase() === "#text" && child.textContent) {
      textContent += child.textContent;
    }
  }
  textContent = textContent.trim();

  if (!textContent && node.textContent) {
    textContent = node.textContent.trim();
  }

  const role = getAttr(attrs, "role");
  const tabindex = getAttr(attrs, "tabindex");
  const onclick = getAttr(attrs, "onclick");
  const styleAttr = getAttr(attrs, "style");

  const isInteractive =
    INTERACTIVE_TAGS.has(rawTag) ||
    onclick !== undefined ||
    (role ?? "").toLowerCase() === "button" ||
    (tabindex ?? "") !== "";

  if (!textContent && isInteractive) {
    textContent = collectDeepText(node, 100).trim();
  }
  textContent = textContent.slice(0, 100);

  const element: ElementData = {
    tag: rawTag,
    id: getAttr(attrs, "id") ?? null,
    className: getAttr(attrs, "class") ?? null,
    text: textContent || null,
    href: getAttr(attrs, "href") ?? null,
    ariaLabel: getAttr(attrs, "aria-label") ?? null,
    placeholder: getAttr(attrs, "placeholder") ?? null,
    x: parsePx(getAttr(attrs, "data-ax-x")),
    y: parsePx(getAttr(attrs, "data-ax-y")),
    width: parsePx(getAttr(attrs, "data-ax-width")),
    height: parsePx(getAttr(attrs, "data-ax-height")),
    isVisible: true,
    isInteractive,
    isButton: BUTTON_TAGS.has(rawTag),
    isInput: INPUT_TAGS.has(rawTag),
    isLink: LINK_TAGS.has(rawTag),
    isFixed:
      typeof styleAttr === "string" &&
      styleAttr.includes("position") &&
      styleAttr.includes("fixed"),
    hasAnimation:
      typeof styleAttr === "string" &&
      (styleAttr.includes("animation") || styleAttr.includes("transition")),
    childCount: children.filter(
      (c) => (c.tagName ?? "").toLowerCase() !== "#text",
    ).length,
    depth,
    parentIdx,
  };

  const currentIdx = elements.length;
  elements.push(element);

  for (const child of children) {
    if (elements.length >= WALK_CAP) return;
    walkNode(child, elements, currentIdx, depth + 1);
  }
}

function prioritiseElements(
  all: ElementData[],
  clicks: ClickPosition[],
): ElementData[] {
  const t0: ElementData[] = [];
  const t1: ElementData[] = [];
  const t2: ElementData[] = [];
  const t3: ElementData[] = [];
  const t4: ElementData[] = [];

  for (const el of all) {
    const hasBounds = el.width > 0 && el.height > 0;
    const containsClick = hasBounds && elementContainsAnyClick(el, clicks);

    if (containsClick && el.isInteractive) {
      t0.push(el);
    } else if (containsClick) {
      t1.push(el);
    } else if (el.isInteractive && hasBounds) {
      t2.push(el);
    } else if (hasBounds) {
      t3.push(el);
    } else {
      t4.push(el);
    }
  }

  const byArea = (a: ElementData, b: ElementData) =>
    a.width * a.height - b.width * b.height;
  t0.sort(byArea);
  t1.sort(byArea);
  t2.sort(byArea);
  t3.sort(byArea);

  const selected = [...t0, ...t1, ...t2, ...t3, ...t4].slice(0, MAX_ELEMENTS);

  const oldToNew = new Map<number, number>();
  const allIndexOf = new Map<ElementData, number>();
  for (let i = 0; i < all.length; i++) allIndexOf.set(all[i], i);
  for (let i = 0; i < selected.length; i++) {
    const origIdx = allIndexOf.get(selected[i]) ?? -1;
    oldToNew.set(origIdx, i);
  }
  for (const el of selected) {
    el.parentIdx = oldToNew.get(el.parentIdx ?? -1) ?? -1;
  }

  return selected;
}

function elementContainsAnyClick(
  el: ElementData,
  clicks: ClickPosition[],
): boolean {
  if (clicks.length === 0) return false;
  for (const c of clicks) {
    if (
      c.x >= el.x &&
      c.x <= el.x + el.width &&
      c.y >= el.y &&
      c.y <= el.y + el.height
    ) {
      return true;
    }
  }
  return false;
}
